import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DisableOperatorResponse, Operator, OperatorListResponse,
  OperatorMutationResponse, OperatorRole,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { ConfirmActionDialog } from '../components/ConfirmActionDialog'
import { RelativeTime } from '../components/RelativeTime'
import { useAdminMutation } from '../hooks/useAdminMutation'

// plan-admin/10 — Operator Management page (admin-only).
//
// One table; mutations route through ConfirmActionDialog so every
// audited write captures a free-text reason. Role gating is layered:
//
//   1. The route-level RequireRole gate (router.tsx) enforces ADMIN
//      before this component renders.
//   2. The page itself shows a friendly notice instead of a table when
//      a non-admin somehow reaches it (defence-in-depth in case the
//      router gate is misconfigured).
//   3. Self-mutating buttons (disable / role-change to a lower role)
//      are disabled client-side as well as server-side. The backend
//      is the source of truth (SELF_DISABLE_FORBIDDEN /
//      SELF_DEMOTE_FORBIDDEN), but the client guard keeps the failure
//      mode out of the audit log when an admin clicks accidentally.

const ROLE_OPTIONS: OperatorRole[] = ['VIEWER', 'OPERATOR', 'ADMIN']
const ROLE_RANK: Record<OperatorRole, number> = {
  VIEWER: 0, OPERATOR: 1, ADMIN: 2,
}

interface CreateDraft {
  google_sub: string
  email: string
  display_name: string
  role: OperatorRole
}

const EMPTY_CREATE: CreateDraft = {
  google_sub: '', email: '', display_name: '', role: 'OPERATOR',
}

interface PendingRoleChange {
  operator: Operator
  newRole: OperatorRole
}

interface PendingDisable {
  operator: Operator
}

interface PendingEnable {
  operator: Operator
}

interface PendingCreate {
  draft: CreateDraft
}

export function OperatorManagementPage() {
  const auth = useAuth()
  const client = useAdminApiClient()
  const isAdmin = auth.requireRole('ADMIN')
  const selfId = auth.status === 'authenticated' ? auth.operator.operator_id : ''

  const list = useQuery({
    queryKey: queryKeys.operators(),
    queryFn: () => client.get<OperatorListResponse>('/r/admin/operators'),
    enabled: isAdmin,
  })
  const operators = list.data?.success ? list.data.data : []
  // plan-admin/10 review H2 (HEAD ``8f52daf``): we render the
  // creator as a friendly name when the actor is itself an
  // operator we currently know about; otherwise fall back to the
  // raw id so an admin who provisioned then deleted the creating
  // account still sees provenance.
  const operatorsById: Record<string, Operator> = {}
  for (const op of operators) {
    operatorsById[op.operator_id] = op
  }

  const [createDraft, setCreateDraft] = useState<CreateDraft>(EMPTY_CREATE)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(null)
  const [pendingRole, setPendingRole] = useState<PendingRoleChange | null>(null)
  const [pendingDisable, setPendingDisable] = useState<PendingDisable | null>(null)
  const [pendingEnable, setPendingEnable] = useState<PendingEnable | null>(null)

  const createMutation = useAdminMutation<
    CreateDraft & { reason: string }, Operator
  >({
    mutate: (c, vars) =>
      c.post<OperatorMutationResponse>('/r/admin/operators', vars),
    invalidates: [queryKeys.operators()],
  })

  const roleMutation = useAdminMutation<
    { operator_id: string; role: OperatorRole; reason: string },
    Operator
  >({
    mutate: (c, vars) =>
      c.post<OperatorMutationResponse>(
        `/r/admin/operators/${vars.operator_id}/role`,
        { role: vars.role, reason: vars.reason },
      ),
    invalidates: [queryKeys.operators()],
  })

  const disableMutation = useAdminMutation<
    { operator_id: string; reason: string },
    DisableOperatorResponse['data']
  >({
    mutate: (c, vars) =>
      c.post<DisableOperatorResponse>(
        `/r/admin/operators/${vars.operator_id}/disable`,
        { reason: vars.reason },
      ),
    invalidates: [queryKeys.operators()],
  })

  const enableMutation = useAdminMutation<
    { operator_id: string; reason: string }, Operator
  >({
    mutate: (c, vars) =>
      c.post<OperatorMutationResponse>(
        `/r/admin/operators/${vars.operator_id}/enable`,
        { reason: vars.reason },
      ),
    invalidates: [queryKeys.operators()],
  })

  if (!isAdmin) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-xl font-semibold text-stone-900">Operators</h1>
        <div
          className="rounded-md border border-stone-200 bg-stone-50 p-4
                     text-sm text-stone-700"
          data-testid="admin-required-notice"
        >
          Admin role required to manage operators. Sign in as an admin
          to view this page.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Operators</h1>
          <p className="mt-1 text-sm text-stone-500">
            Provision admin operators, change roles, and disable
            access. Every mutation is recorded in the audit log.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setCreateDraft(EMPTY_CREATE)
            setShowCreateForm(true)
          }}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium
                     text-white hover:bg-accent/90"
        >
          Add operator
        </button>
      </header>

      {list.isLoading && (
        <p className="text-sm text-stone-500">Loading operators…</p>
      )}
      {list.isError && (
        <div
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm
                     text-red-800"
          role="alert"
        >
          Failed to load operators. Try refreshing.
        </div>
      )}

      {!list.isLoading && !list.isError && operators.length === 0 && (
        <p className="text-sm text-stone-500">No operators provisioned yet.</p>
      )}

      {operators.length > 0 && (
        <table
          data-testid="operators-table"
          className="w-full table-auto border-collapse rounded-lg border
                     border-stone-200 bg-white text-sm"
        >
          <thead className="bg-stone-50 text-left text-xs uppercase
                            tracking-wide text-stone-500">
            <tr>
              <th className="px-3 py-2">Operator</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2">Created by</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((op) => (
              <OperatorRow
                key={op.operator_id}
                op={op}
                isSelf={op.operator_id === selfId}
                operatorsById={operatorsById}
                onRoleChange={(role) => setPendingRole({ operator: op, newRole: role })}
                onDisable={() => setPendingDisable({ operator: op })}
                onEnable={() => setPendingEnable({ operator: op })}
              />
            ))}
          </tbody>
        </table>
      )}

      {showCreateForm && (
        <CreateOperatorForm
          draft={createDraft}
          setDraft={setCreateDraft}
          onCancel={() => setShowCreateForm(false)}
          onSubmit={(draft) => {
            setShowCreateForm(false)
            setPendingCreate({ draft })
          }}
        />
      )}

      {pendingCreate && (
        <ConfirmActionDialog
          title="Add operator"
          body={
            <span>
              Provision <strong>{pendingCreate.draft.display_name}</strong> as
              a <strong>{pendingCreate.draft.role}</strong> operator. The
              new operator can sign in immediately.
            </span>
          }
          confirmLabel="Add operator"
          onConfirm={async ({ reason }) => {
            await createMutation.mutateAsync({
              ...pendingCreate.draft, reason,
            })
            setPendingCreate(null)
          }}
          onCancel={() => setPendingCreate(null)}
        />
      )}

      {pendingRole && (
        <ConfirmActionDialog
          title="Change role"
          body={
            <span>
              Change <strong>{pendingRole.operator.display_name}</strong>{' '}
              from <strong>{pendingRole.operator.role}</strong> to{' '}
              <strong>{pendingRole.newRole}</strong>. The operator's
              existing session continues; the new role applies on the
              next privileged action.
            </span>
          }
          confirmLabel="Change role"
          onConfirm={async ({ reason }) => {
            await roleMutation.mutateAsync({
              operator_id: pendingRole.operator.operator_id,
              role: pendingRole.newRole,
              reason,
            })
            setPendingRole(null)
          }}
          onCancel={() => setPendingRole(null)}
        />
      )}

      {pendingDisable && (
        <ConfirmActionDialog
          title="Disable operator"
          body={
            <span>
              Disable <strong>{pendingDisable.operator.display_name}</strong>{' '}
              and revoke every active session for this operator
              immediately. They can be re-enabled later from this page.
            </span>
          }
          confirmLabel="Disable operator"
          onConfirm={async ({ reason }) => {
            await disableMutation.mutateAsync({
              operator_id: pendingDisable.operator.operator_id,
              reason,
            })
            setPendingDisable(null)
          }}
          onCancel={() => setPendingDisable(null)}
        />
      )}

      {pendingEnable && (
        <ConfirmActionDialog
          title="Enable operator"
          body={
            <span>
              Re-enable <strong>{pendingEnable.operator.display_name}</strong>.
              The operator must sign in again before they can act.
            </span>
          }
          confirmLabel="Enable operator"
          onConfirm={async ({ reason }) => {
            await enableMutation.mutateAsync({
              operator_id: pendingEnable.operator.operator_id,
              reason,
            })
            setPendingEnable(null)
          }}
          onCancel={() => setPendingEnable(null)}
        />
      )}
    </div>
  )
}

interface OperatorRowProps {
  op: Operator
  isSelf: boolean
  operatorsById: Record<string, Operator>
  onRoleChange: (role: OperatorRole) => void
  onDisable: () => void
  onEnable: () => void
}

function OperatorRow({
  op, isSelf, operatorsById, onRoleChange, onDisable, onEnable,
}: OperatorRowProps) {
  const selectId = `role-${op.operator_id}`
  return (
    <tr
      data-testid={`operator-row-${op.operator_id}`}
      className="border-t border-stone-200 align-top"
    >
      <td className="px-3 py-2">
        <div className="font-medium text-stone-900">
          {op.display_name || op.email}
        </div>
        <div className="text-xs text-stone-500">{op.email}</div>
        {isSelf && (
          <div className="mt-1 text-xs font-semibold text-amber-700">
            This is you
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <label
          htmlFor={selectId}
          className="text-xs uppercase tracking-wide text-stone-500"
        >
          Role
        </label>
        <select
          id={selectId}
          value={op.role}
          onChange={(e) => {
            const next = e.target.value as OperatorRole
            if (next === op.role) return
            // Block self-demote client-side as a UX guard; server is
            // still the source of truth (SELF_DEMOTE_FORBIDDEN).
            if (isSelf && ROLE_RANK[next] < ROLE_RANK[op.role]) return
            onRoleChange(next)
          }}
          className="mt-1 block rounded border border-stone-300 px-2 py-1 text-sm"
        >
          {ROLE_OPTIONS.map((role) => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-stone-700">
        {op.last_login_time > 0
          ? <RelativeTime timestamp={op.last_login_time} />
          : <span className="text-xs text-stone-400">never</span>}
      </td>
      <td
        className="px-3 py-2 text-stone-700"
        data-testid={`operator-row-${op.operator_id}-created-by`}
      >
        <CreatedByCell op={op} operatorsById={operatorsById} />
      </td>
      <td className="px-3 py-2">
        {op.disabled
          ? <span
              className="rounded-full bg-red-100 px-2 py-0.5 text-xs
                         font-semibold text-red-800"
            >disabled</span>
          : <span
              className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs
                         font-semibold text-emerald-900"
            >active</span>}
      </td>
      <td className="px-3 py-2">
        {op.disabled ? (
          <button
            type="button"
            onClick={onEnable}
            className="rounded-md border border-stone-300 px-2 py-1 text-xs
                       font-medium text-stone-700 hover:bg-stone-50"
          >
            Enable
          </button>
        ) : (
          <button
            type="button"
            onClick={onDisable}
            disabled={isSelf}
            className="rounded-md border border-stone-300 px-2 py-1 text-xs
                       font-medium text-red-700 hover:bg-red-50
                       disabled:cursor-not-allowed disabled:text-stone-400"
          >
            Disable
          </button>
        )}
      </td>
    </tr>
  )
}

interface CreatedByCellProps {
  op: Operator
  operatorsById: Record<string, Operator>
}

// Render the operator-management "Created by" column. The plan
// records ``created_by_operator_id``; we resolve it to a human-
// readable name when the creator is still a known operator and
// otherwise fall back to the raw id (so a deleted-creator history
// is still visible). Empty values come from legacy records and
// the bootstrap CLI — those render as a quiet em dash so the
// column never shows a misleading "None" / "—" without context.
function CreatedByCell({ op, operatorsById }: CreatedByCellProps) {
  const creatorId = op.created_by_operator_id
  if (!creatorId) {
    return <span className="text-xs text-stone-400">—</span>
  }
  const creator = operatorsById[creatorId]
  if (creator) {
    return (
      <div>
        <div className="text-stone-900">{creator.display_name || creator.email}</div>
        <div className="text-xs text-stone-500">{creator.email}</div>
      </div>
    )
  }
  return (
    <span
      className="font-mono text-xs text-stone-600"
      title="Creator no longer in the operator list"
    >
      {creatorId}
    </span>
  )
}

interface CreateOperatorFormProps {
  draft: CreateDraft
  setDraft: (draft: CreateDraft) => void
  onCancel: () => void
  onSubmit: (draft: CreateDraft) => void
}

function CreateOperatorForm({
  draft, setDraft, onCancel, onSubmit,
}: CreateOperatorFormProps) {
  const isValid = draft.google_sub.trim() && draft.email.trim()
                  && draft.display_name.trim()
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (isValid) onSubmit(draft)
      }}
      className="space-y-3 rounded-lg border border-stone-200 bg-stone-50 p-4"
    >
      <h2 className="text-sm font-semibold text-stone-800">Add operator</h2>
      <label className="block text-xs text-stone-700">
        Google sub
        <input
          type="text"
          value={draft.google_sub}
          onChange={(e) => setDraft({ ...draft, google_sub: e.target.value })}
          className="mt-1 block w-full rounded border border-stone-300 px-2 py-1 text-sm"
          required
        />
      </label>
      <label className="block text-xs text-stone-700">
        Email
        <input
          type="email"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          className="mt-1 block w-full rounded border border-stone-300 px-2 py-1 text-sm"
          required
        />
      </label>
      <label className="block text-xs text-stone-700">
        Display name
        <input
          type="text"
          value={draft.display_name}
          onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
          className="mt-1 block w-full rounded border border-stone-300 px-2 py-1 text-sm"
          required
        />
      </label>
      <label className="block text-xs text-stone-700">
        Role
        <select
          value={draft.role}
          onChange={(e) => setDraft({ ...draft, role: e.target.value as OperatorRole })}
          className="mt-1 block rounded border border-stone-300 px-2 py-1 text-sm"
        >
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={!isValid}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium
                     text-white hover:bg-accent/90 disabled:bg-stone-300"
        >
          Submit
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-stone-300 px-3 py-1.5 text-sm
                     font-medium text-stone-700 hover:bg-stone-100"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
